import type {Notice} from "@/types";
import axios from "axios";
import {defineStore} from "pinia";
import {computed, Reactive, reactive, ref} from "vue";
import {handleError} from "./storeHelper";
import {NoticeItem, ScheduledItem} from "../components/Notices/types";
import {SelectItem} from "../types";
import {NoticeModel} from "../components/Notices/types";
import {
    BFilter,
    BFilterableField,
    BFilterOptions,
    dateTimeOperations,
    listOperations,
    textOperations,
} from "../components/BFilterEditor/BFilterEditor.types";
import {useOriStore} from "./oriStore";
import {serializeFilters, serializeSortBy} from "../components/BTable/HelperFunctions";
import {BTableColumn, BTableOptions} from "@/components/BTable/BTable.types";

const oriStore = useOriStore();

export const useNoticeStore = defineStore("noticeStore", () => {
    const apiUrl = "api/v1/notice";

    const loading = ref<boolean>(false);

    const items = ref<Notice[]>([]);
    const noticeItems = ref([] as NoticeItem[]);

    var recipients = ref([] as any[]);

    const noticeFilterOptions = ref<BFilterOptions>({
        filters: [],
    });

    const filterableNoticeFields = reactive<BFilterableField[]>([
        {
            property: "agencyList",
            label: "Agency",
            operations: listOperations,
            editorComponent: "list-editor",
            editorOptions: oriStore.getOrisAsBFieldSelectionArray,
        },
        {
            property: "runDate",
            label: "Date",
            operations: dateTimeOperations,
            editorComponent: "date-editor",
        },
        {
            property: "description",
            label: "Description",
            operations: textOperations,
            editorComponent: "text-editor",
        },
        {
            property: "dismissed",
            label: "Dismissed",
            operations: listOperations,
            editorComponent: "list-editor",
            editorOptions: [
                {text: "True", value: "true"},
                {text: "False", value: "false"},
            ],
        },
    ]);

    var allNoticesTableOptions = ref({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [
            {
                key: "run_date",
                isDescending: true,
            },
        ],
        pagingMode: "noTotal",
    } as BTableOptions);

    const recipientFilterOptions = ref<BFilterOptions>({
        filters: [],
    });

    const recipientFilterableFields = reactive<BFilterableField[]>([
        {
            property: "agency",
            label: "Agency",
            operations: listOperations,
            editorComponent: "list-editor",
            editorOptions: oriStore.getOrisAsBFieldSelectionArray,
        },
        {
            property: "email",
            label: "Email",
            operations: textOperations,
            editorComponent: "text-editor",
        },
        {
            property: "is_dismissed",
            label: "Dismissed",
            operations: listOperations,
            editorComponent: "list-editor",
            editorOptions: [
                {text: "True", value: "true"},
                {text: "False", value: "false"},
            ],
        },
    ]);

    var recipientTableOptions = ref({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [
            {
                key: "agency",
                isDescending: false,
            },
        ],
    } as BTableOptions);

    const list = async (ori: string) => {
        try {
            loading.value = true;
            const response = await axios.get<Notice[]>(`${apiUrl}/ori/${ori}`);
            items.value = response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }        
    };

    const allNotices = async () => {
        try {
            loading.value = true;
            let filterParam = serializeFilters(noticeFilterOptions.value.filters, filterableNoticeFields);
            let sortOptionsText = serializeSortBy(allNoticesTableOptions.value.sortBy);

            let response = await axios.get<NoticeItem[]>("api/v1/notice-log", {
                params: {
                    filter: filterParam,
                    sort: sortOptionsText,
                    page: allNoticesTableOptions.value.page,
                    pageSize: allNoticesTableOptions.value.itemsPerPage,
                    pagingMode: allNoticesTableOptions.value.pagingMode,
                },
            });

            if (response.status == 200) {
                noticeItems.value = response.data;
            } else {
                noticeItems.value = [];
            }
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getNoticeLog = (id: number) => {
        try {
            return axios.get<NoticeItem>(`api/v1/notice-log/${id}`);
        } catch (error) {
            handleError(error);
            throw error;
        }
    };

    const dismissNotice = (id: Number) => {
        try {
            loading.value = true;
            return axios.post(`${apiUrl}/${id}`);
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const dismissNoticesByLogId = (id: Number, ori?: string) => {
        try {
            loading.value = true;
            return axios.post("api/v1/notice-log/dismiss", {id: id, ori: ori ?? ""});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getNoticeRecipients = async (id: number) => {
        try {
            loading.value = true;

            let filterParam = serializeFilters(recipientFilterOptions.value.filters, recipientFilterableFields);
            let sortOptionsText = serializeSortBy(recipientTableOptions.value.sortBy);

            var response = await axios.get<NoticeItem[]>(`${apiUrl}/recipient-list/${id}`, {
                params: {
                    filter: filterParam,
                    sort: sortOptionsText,
                    page: recipientTableOptions.value.page,
                    pageSize: recipientTableOptions.value.itemsPerPage,
                    pagingMode: recipientTableOptions.value.pagingMode,
                },
            });

            if (response != undefined && response.status == 200 && response.data != undefined) {
                recipients.value = response.data;
                recipientTableOptions.value.totalItemCount = recipients.value.length;
            } else {
                recipients.value = [];
            }
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const getNoticeRecipientsForOriAsString = async (id: number, ori: string) => {
        try {
            loading.value = true;

            let oriFilter = [{property: "agency", operation: "eq", value: ori}] as BFilter[];
            let filterParam = serializeFilters(oriFilter, recipientFilterableFields);

            let response = await axios.get<NoticeItem[]>(`${apiUrl}/recipient-list/${id}`, {
                params: {
                    filter: filterParam,
                },
            });

            let email = response.data.map((it) => it.email).join(", ");

            return email;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const columns = computed(() => {
        let data: BTableColumn[] = [];

        data.push(
            {key: "agencyList", text: "Agency", sortable: false, value: (item) => item.agencyList},
            {key: "runDate", text: "Date", sortable: false, value: (item) => item.run_date},
            {key: "description", text: "Description", sortable: false, value: (item) => item.description},
            {key: "dismissed", text: "Dismissed", sortable: false, value: (item) => item.is_dismissed}
        );

        return data;
    });

    const resetRecipients = () => {
        recipientFilterOptions.value.filters = [];
        recipients.value = [];
    };

    return {
        noticeFilterOptions,
        filterableNoticeFields,
        loading,
        items,
        noticeItems,
        list,
        allNotices,
        dismissNotice,
        getNoticeRecipients,
        dismissNoticesByLogId,
        getNoticeLog,
        columns,
        allNoticesTableOptions,
        recipientFilterableFields,
        recipientFilterOptions,
        recipientTableOptions,
        recipients,
        resetRecipients,
        getNoticeRecipientsForOriAsString,
        apiUrl,
    };
});
